import { useState } from 'react';
import useDownloadDocumentsSub4Mutation from './useDownloadDocumentsSub4Mutation';
import { always, any, applySpec, compose, concat, defaultTo, equals, filter, find, ifElse, isEmpty, map, path, pathEq, pathOr, pluck, prop, propEq, propOr, reject, replace, slice, toString, when } from 'ramda';
import useSendMailSouscriptionRecapMutationQuery from './useSendMailSouscriptionRecapMutationQuery.js';
import { useForm } from 'react-hook-form';
import { formatPhone } from '@@viewHelpers/phone';
import { attachedToOptions, civilityOptions, getLabelFromValue } from '@@viewCommonComponents/forms/options';
import { datePathTsToFr } from '@@viewHelpers/date';
import { CENTER_CODE_INDEXES, FUND_CODE_INDEXES, REGIME_CODE_INDEXES } from '@@viewCommonComponents/controlled/ControlledSocialSecurityOrganismCode/useControlledSocialSecurityOrganismCode';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { formatLegendAndLabel, getIsSelected, getPropForCode, getValue } from '@@viewHelpers/getPropsGuarantees.js';
import { formatSSNumberKey, isNotNilOrEmpty } from '@@serverHelpers';
import { attachedToEnum } from '@@viewEnums';

const useSubscription4Form = ({
  regimeObligatoire,
  getMiddleText,
  getMiddleBoolean,
  familySituations = [],
  professionalSituations = [],
  jobs = [],
  socioProfessionalCategories = [],
  activities = [],
  project
}) => {
  const [displayError, setDisplayError] = useState('');
  const [canSendMail, setCanSendMail] = useState(project.canSendMail);
  const {
    processHandlers,
    context
  } = useProcess();

  const { onFormSubmit: downloadMutation } = useDownloadDocumentsSub4Mutation();
  const { onFormSubmit: sendEmailMutation } = useSendMailSouscriptionRecapMutationQuery();

  const {
    documentsToUpload,
    isDutyOfAdviceSkipped,
    isPaperSignatureMode,
    offerCode
  } = applySpec({
    documentsToUpload: propOr([], 'documentsToUpload'),
    isDutyOfAdviceSkipped: pathOr(false, ['collectionNeeds', 0, 'isDutyOfAdviceSkipped']),
    isPaperSignatureMode: pathEq(['signature', 'mode'], 'MANUAL'),
    offerCode: path(['offer', 'code'])
  })(project);

  const documentsSelected = pluck('option', documentsToUpload);

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      documentsSelected: documentsSelected
    }
  });

  const isProjectEditable = prop('isProjectEditable', project);
  const canGoNext =
    isProjectEditable || (isPaperSignatureMode && !propEq('status', 'SIGNED', project));

  const onProjectNotEditable = () => {
    setDisplayError('Vous ne pouvez pas modifier ce projet');
    return setTimeout(() => setDisplayError(''), 3000);
  };

  const onSubmit = () => {
    if (canGoNext) {
      processHandlers.onNext({
        context: {
          projectId: context?.projectId
        }
      });
    } else {
      onProjectNotEditable();
    }
  };

  const editDetails = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_EDIT_BENEFICIARY'
      }
    });
  };

  const editSouscription3 = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const editContract = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId,
        action: 'ON_EDIT_CONTRACT'
      }
    });
  };

  const customer = propOr({}, 'customer', project);
  const regimeCode = slice(
    REGIME_CODE_INDEXES.FROM,
    REGIME_CODE_INDEXES.TO,
    pathOr('', ['socialSecurities', 0, 'organismCode'], customer)
  );
  const caseNumber = slice(
    FUND_CODE_INDEXES.FROM,
    FUND_CODE_INDEXES.TO,
    pathOr('', ['socialSecurities', 0, 'organismCode'], customer)
  );
  const centerCode = slice(
    CENTER_CODE_INDEXES.FROM,
    CENTER_CODE_INDEXES.TO,
    pathOr('', ['socialSecurities', 0, 'organismCode'], customer)
  );

  const extracRegimeEnum = replace('REGIME_PRO_', '');

  const customerCardOptions = [
    {
      displayed:
        getMiddleBoolean('ADHERENT_CIVILITE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_CIVILITE_LABEL'),
      value: getLabelFromValue(prop('civility', customer), civilityOptions)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_NOM_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_NOM_LABEL'),
      value: propOr('', 'lastName', customer)
    },
    {
      displayed: getMiddleBoolean('ADHERENT_NOM_NAISSANCE_EST_NECESSAIRE'),
      label: getMiddleText('ADHERENT_NOM_NAISSANCE_LABEL'),
      value: propOr('', 'birthName', customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_PRENOM_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_PRENOM_LABEL'),
      value: propOr('', 'firstName', customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_DATE_NAISSANCE_LABEL'),
      value: datePathTsToFr('birthDate', customer)
    },
    {
      displayed: true,
      label: getMiddleText('MEMBER_FAMILY_SITUATION_LABEL'),
      value: getLabelFromValue(prop('maritalStatus', customer), familySituations)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_SIT_PRO_LABEL'),
      value: getLabelFromValue(
        pathOr('', ['CSPs', 0, 'categoryLvl1CSPExternalId'], customer),
        professionalSituations
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_CSP_LABEL'),
      value: getLabelFromValue(
        pathOr('', ['CSPs', 0, 'categoryCSPExternalId'], customer),
        socioProfessionalCategories
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT'),
      label: getMiddleText('ADHERENT_PROFESSION_LABEL'),
      value: getLabelFromValue(path(['CSPs', 0, 'externalId'], customer), jobs)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_CAISSE_PRO_LABEL'),
      value: extracRegimeEnum(pathOr('', ['socialSecurities', 0, 'regime'], customer))
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_ACTIVITE_LABEL'),
      value: getLabelFromValue(propOr('', 'professionalStatus', customer), activities)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: 'Travailleur Non Salarié',
      value: prop('isTNS', customer) ? 'Oui' : 'Non'
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP1'),
      value: pathOr('', ['addresses', 0, 'fields', 0], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP2'),
      value: pathOr('', ['addresses', 0, 'fields', 1], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP3'),
      value: pathOr('', ['addresses', 0, 'fields', 2], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_CODE_POSTAL_LABEL'),
      value: pathOr('', ['addresses', 0, 'zipCode'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_VILLE_LABEL'),
      value: pathOr('', ['addresses', 0, 'city'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_TEL_MOBILE_LABEL'),
      value: formatPhone(propOr([], 'phones', customer), true)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_TEL_FIXE_LABEL'),
      value: formatPhone(propOr([], 'phones', customer), false)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_EMAIL_LABEL'),
      value: pathOr([], ['emails', 0, 'value'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_NSS_LABEL'),
      value: formatSSNumberKey(
        concat(
          pathOr('', ['socialSecurities', 0, 'number'], customer),
          pathOr('', ['socialSecurities', 0, 'key'], customer)
        )
      )
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: getMiddleText('ADHERENT_REGIME_LABEL'),
      value: getLabelFromValue(
        pathOr('-', ['socialSecurities', 0, 'regime'], customer),
        propOr([], 'regimes', regimeObligatoire)
      )
    },
    {
      displayed: getMiddleBoolean('ADHERENT_TELETRANSMISSION_EST_NECESSAIRE'),
      label: getMiddleText('ADHERENT_TELETRANSMISSION_LABEL'),
      value: pathOr(false, ['socialSecurities', 0, 'isTeletransmission'], customer) ? 'Oui' : 'Non'
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ORG_RATT_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: 'Code régime',
      value: regimeCode
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ORG_RATT_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: 'Code caisse',
      value: caseNumber
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_ORG_RATT_EST_NECESSAIRE') &&
        getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
      label: 'Centre',
      value: centerCode
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_SOUSCRIPTION') ||
          getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT')),
      label: getMiddleText('ADHERENT_RANG_LABEL'),
      value: pathOr('-', ['socialSecurities', 0, 'twinsRank'], customer)
    },
    {
      displayed:
        getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
        (getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_SOUSCRIPTION') ||
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')),
      label: getMiddleText('ADHERENT_REVENU_ANNUEL_LABEL'),
      value: concat(pathOr('-', ['finances', 0, 'annualIncome'], customer), ' €')
    },
    {
      displayed: getMiddleBoolean('ADHERENT_OPT_NUM_EST_NECESSAIRE'),
      label: getMiddleText('ADHERENT_OPT_NUM_LABEL'),
      value: prop('isDigitalContract', customer) ? 'Oui' : 'Non'
    }
  ];

  const guarantees = prop('guarantees', project);
  const grantees = propOr([], 'grantees', project);
  const {
    TT_CAUSE_CAP,
    OPTION_TT_CAUSE_DOUBL_ACC,
    OPTION_TT_CAUSE_DOUBL_EFFET,
    OPTION_ACC,
    OPTION_ACC_DOUBL_ACC,
    OPTION_ACC_DOUBL_EFFET,
    RCONJ_CAP,
    REDUC_CAP,
    OPTION_INVAL,
    OPTION_INCAP_CAP,
    OPTION_ZERO_SIN,
    OPTION_EXO_COT,
    OPTION_CONFORT_PSY,
    HOSP_ENF_ALLOW_AMOUNT,
    OPTION_FR_GEN,
    INVAL_DEP_CAP
  } = applySpec({
    TT_CAUSE_CAP: find(propEq('code', 'TT_CAUSE_CAP')),
    OPTION_TT_CAUSE_DOUBL_ACC: find(propEq('code', 'OPTION_TT_CAUSE_DOUBL_ACC')),
    OPTION_TT_CAUSE_DOUBL_EFFET: find(propEq('code', 'OPTION_TT_CAUSE_DOUBL_EFFET')),
    OPTION_ACC: find(propEq('code', 'OPTION_ACC')),
    OPTION_ACC_DOUBL_ACC: find(propEq('code', 'OPTION_ACC_DOUBL_ACC')),
    OPTION_ACC_DOUBL_EFFET: find(propEq('code', 'OPTION_ACC_DOUBL_EFFET')),
    RCONJ_CAP: find(propEq('code', 'RCONJ_CAP')),
    REDUC_CAP: find(propEq('code', 'REDUC_CAP')),
    OPTION_INVAL: find(propEq('code', 'OPTION_INVAL')),
    OPTION_INCAP_CAP: find(propEq('code', 'OPTION_INCAP_CAP')),
    OPTION_ZERO_SIN: find(propEq('code', 'OPTION_ZERO_SIN')),
    OPTION_EXO_COT: find(propEq('code', 'OPTION_EXO_COT')),
    OPTION_CONFORT_PSY: find(propEq('code', 'OPTION_CONFORT_PSY')),
    HOSP_ENF_ALLOW_AMOUNT: find(propEq('code', 'HOSP_ENF_ALLOW_AMOUNT')),
    OPTION_FR_GEN: find(propEq('code', 'OPTION_FR_GEN')),
    INVAL_DEP_CAP: find(propEq('code', 'INVAL_DEP_CAP'))
  })(guarantees || {});

  // PTIADeathPrevTns
  const PTIADeathOptions = [
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE') &&
        getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_CAPITAL_LABEL'),
      value: getPropForCode('label', 'TT_CAUSE_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE') &&
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_CAPITAL_LABEL'),
      value: getPropForCode('label', 'ACC_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_EST_NECESSAIRE'
        ) &&
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        ((getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE') &&
          getIsSelected('OPTION_ACC', guarantees) &&
          getIsSelected('OPTION_ACC_DOUBL_ACC', guarantees)) ||
          (getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE'
          ) &&
            getIsSelected('OPTION_TT_CAUSE', guarantees) &&
            getIsSelected('OPTION_TT_CAUSE_DOUBL_ACC', guarantees)) ||
          (getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE') &&
            getIsSelected('OPTION_ACC', guarantees) &&
            getIsSelected('OPTION_ACC_DOUBL_EFFET', guarantees)) ||
          (getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE'
          ) &&
            getIsSelected('OPTION_TT_CAUSE', guarantees) &&
            getIsSelected('OPTION_TT_CAUSE_DOUBL_EFFET', guarantees))),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_ACC_DOUBL_ACC', guarantees) &&
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_ACC)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_TT_CAUSE_DOUBL_ACC', guarantees) &&
        getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_ACC)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_ACC_DOUBL_EFFET', guarantees) &&
        getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_EFFET)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        getIsSelected('OPTION_TT_CAUSE_DOUBL_EFFET', guarantees) &&
        getIsSelected('OPTION_TT_CAUSE', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_EFFET)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getIsSelected('OPTION_TT_CAUSE', guarantees) || getIsSelected('OPTION_ACC', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_FISCA_MADELIN_LABEL'),
      value: getIsSelected('TT_CAUSE_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const PTIADeathGuarantees = ifElse(
    equals(true),
    always({
      title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_LABEL'),
      price: concat(toString(propOr(0.0, 'annualPrice', TT_CAUSE_CAP)), ' € / an'),
      options: PTIADeathOptions
    }),
    always({
      title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_LABEL'),
      price: concat(toString(propOr(0.0, 'annualPrice', OPTION_ACC)), ' € / an'),
      options: PTIADeathOptions
    })
  )(
    compose(prop('isSelected'), find(propEq('code', 'OPTION_TT_CAUSE')), defaultTo([]))(guarantees)
  );

  // SpousePensionDeathPrevTns
  const spousePensionDeathOptions = [
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_CAPITAL_LABEL'),
      value: getPropForCode('label', 'RCONJ_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: (
        <div className={'flex flex-col gap-1'}>
          <div className={'font-bold'}>Conjoint</div>
          <div>Né le {compose(prop('birthDate'), find(propEq('type', 'SPOUSE')))(grantees)}</div>
        </div>
      )
    },
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_FISCA_MADELIN_LABEL'),
      value: getIsSelected('RCONJ_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const spousePensionGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_RCONJ_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', RCONJ_CAP)), ' € / an'),
    options: spousePensionDeathOptions
  };

  // EducationPensionDeathPrevTns
  const educationPensionDeathOptions = [
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_CAPITAL_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed: true,
      label: (
        <div>
          {map((leg) => <div key={leg}>{leg}</div>)(
            formatLegendAndLabel('legend', 'REDUC_CAP', guarantees)
          )}
        </div>
      ),
      value: (
        <div>
          {map((val) => <div key={val}>{val}</div>)(
            formatLegendAndLabel('label', 'REDUC_CAP', guarantees)
          )}
        </div>
      ),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: (
        <div>
          <div className={'font-bold'}>Enfants</div>
          {map((child) => (
            <div className={'font-thin'} key={child.birthDate}>
              Né le {prop('birthDate')(child)}
            </div>
          ))(filter(propEq('type', 'CHILD'))(grantees))}
        </div>
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_FISCA_MADELIN_LABEL'),
      value: getIsSelected('REDUC_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    }
  ];

  const educationPensionGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', REDUC_CAP)), ' € / an'),
    options: educationPensionDeathOptions
  };

  // INCAPACITE
  const disabilityOptions = [
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) ||
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE') ||
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
        ) ||
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE'),
      label: 'Franchise',
      classNameLabel: 'font-bold'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_MALADIE_EST_NECESSAIRE'
      ),
      label: 'En cas de maladie',
      value: getPropForCode('label', 'OPTION_FR_MAL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
      ),
      label: 'En cas d\'accident',
      value: getPropForCode('label', 'OPTION_FR_ACC')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
      ),
      label: 'En cas d\'hospitalisation',
      value: getPropForCode('label', 'OPTION_FR_HOSP')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getMiddleBoolean(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
      ),
      label: 'Toutes causes',
      value: getPropForCode('label', 'OPTION_FR_ALL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: 'Montant revenu garanti',
      value: getPropForCode('label', 'OPTION_INCAP_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_FISCA_MADELIN_LABEL'),
      value: getIsSelected('INC_MAD', guarantees) ? 'Oui' : 'Non',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EST_NECESSAIRE') &&
        (getIsSelected('OPTION_ZERO_SIN', guarantees) ||
          getIsSelected('OPTION_EXO_COT', guarantees) ||
          getIsSelected('OPTION_CONFORT_PSY', guarantees) ||
          getIsSelected('OPTION_HOSP_ENF_GAR', guarantees)),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_ZERO_SIN', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_ZERO_SINISTRE_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_ZERO_SIN)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_EXO_COT', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_EXO_COTISATION_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_EXO_COT)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_CONFORT_PSY', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_CONFORT_PSY_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_CONFORT_PSY)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE') &&
        getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', HOSP_ENF_ALLOW_AMOUNT)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE') &&
        getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: (
        <div>
          <div className={'font-bold'}>Enfants</div>
          {map((child) => (
            <div className={'font-thin'} key={child.birthDate}>
              Né le {prop('birthDate')(child)}
            </div>
          ))(filter(propEq('type', 'CHILD'))(grantees))}
        </div>
      ),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE') &&
        getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_MT_IJ_LABEL'),
      value: getPropForCode('label', 'HOSP_ENF_ALLOW_AMOUNT')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_EST_NECESSAIRE') &&
        getIsSelected('OPTION_HOSP_ENF_GAR', guarantees),
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_FISCA_MADELIN_LABEL'
      ),
      value: getIsSelected('HOSP_ENF_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', OPTION_FR_GEN)), ' €'),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    },
    {
      displayed:
        (getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_MALADIE_EST_NECESSAIRE'
          ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
          ) ||
          getMiddleBoolean(
            'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
          )) &&
        getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Franchise',
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_MALADIE_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas de maladie',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_MAL')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_ACCIDENT_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas d\'accident',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_ACC')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_HOSPITALISATION_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'En cas d\'hospitalisation',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_HOSP')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_FRAIS_GENERAUX_FRANCHISE_TTES_CAUSES_EST_NECESSAIRE'
        ) && getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Toutes causes',
      value: getPropForCode('label', 'OPTION_FR_GEN_FR_TT_CAUSE')(guarantees),
      classNameValue: 'text-right'
    },
    {
      displayed: getIsSelected('OPTION_FR_GEN', guarantees),
      label: 'Montant revenu garanti',
      value: getPropForCode('label', 'FR_GEN_CAP')(guarantees),
      classNameLabel: 'font-bold',
      classNameValue: 'text-right font-bold'
    }
  ];

  const disabilityGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INCAP_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', OPTION_INCAP_CAP)), ' € / an'),
    options: disabilityOptions
  };

  // INVALIDITE
  const invalidityOptions = [
    {
      displayed: true,
      label: (
        <div>
          {map((leg) => <div key={leg}>{leg}</div>)(
            formatLegendAndLabel('legend', 'INVAL_CAP', guarantees)
          )}
        </div>
      ),
      value: (
        <div>
          {map((val) => <div key={val}>{val}</div>)(
            formatLegendAndLabel('label', 'INVAL_CAP', guarantees)
          )}
        </div>
      ),
      classNameValue: 'text-right'
    },
    {
      displayed: true,
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_FISCA_MADELIN_LABEL'),
      value: getIsSelected('INVAL_MAD', guarantees) ? 'Oui' : 'Non',
      classNameLabel: 'font-bold',
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_EST_NECESSAIRE') &&
        getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_LABEL'),
      classNameLabel: 'font-bold'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_NECESSAIRE') &&
        getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_LABEL'),
      value: concat(toString(propOr(0.0, 'annualPrice', INVAL_DEP_CAP)), ' €'),
      classNameValue: 'text-right'
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_EST_NECESSAIRE') &&
        getIsSelected('OPTION_INVAL_DEP', guarantees),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_OPTION_DEPENDANCE_CAPITAL_LABEL'),
      value: concat(getValue('INVAL_DEP_CAP', guarantees) || '-', ' €'),
      classNameValue: 'text-right'
    }
  ];

  const invalidityGuarantees = {
    title: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_INVAL_LABEL'),
    price: concat(toString(propOr(0.0, 'annualPrice', OPTION_INVAL)), ' € / an'),
    options: invalidityOptions
  };

  const displayRefundInformation =
    isNotNilOrEmpty(path(['customer', 'bankAccounts'], project)) ||
    any(
      (grantee) => isNotNilOrEmpty(propOr([], 'bankAccounts', grantee)),
      propOr([], 'grantees', project)
    );

  const getSlicedOrganismCode = (from, to) =>
    compose(
      when(isEmpty, always('-')),
      slice(from, to),
      pathOr('', ['socialSecurities', 0, 'organismCode'])
    );
  const getRegimeCode = getSlicedOrganismCode(REGIME_CODE_INDEXES.FROM, REGIME_CODE_INDEXES.TO);
  const getCaseNumber = getSlicedOrganismCode(FUND_CODE_INDEXES.FROM, FUND_CODE_INDEXES.TO);
  const getCenterCode = getSlicedOrganismCode(CENTER_CODE_INDEXES.FROM, CENTER_CODE_INDEXES.TO);
  const granteesCardOptions = (grantee) => {
    const attachedToSelf = equals(
      attachedToEnum.EN_NOM_PROPRE,
      path(['attachedSocialSecurities', 0, 'type'], grantee)
    );

    const granteeCardOptions = [
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_TYPE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_TYPE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_TYPE_LABEL'),
        value: getMiddleText(`BENEFICIAIRE_TYPE_${grantee.type}_LABEL`)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_CIVILITE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_CIVILITE_LABEL'),
        value: getLabelFromValue(prop('civility', grantee), civilityOptions)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_NOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_NOM_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_NOM_LABEL'),
        value: propOr('', 'lastName', grantee)
      },
      {
        displayed: getMiddleBoolean('BENEFICIAIRE_NOM_NAISSANCE_EST_NECESSAIRE'),
        label: getMiddleText('BENEFICIAIRE_NOM_NAISSANCE_LABEL'),
        value: propOr('', 'birthName', grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_PRENOM_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_PRENOM_LABEL'),
        value: propOr('', 'firstName', grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_DATE_NAISSANCE_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_DATE_NAISSANCE_LABEL'),
        value: propOr('', 'birthDate', grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION') ||
            getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT')),
        label: getMiddleText('BENEFICIAIRE_REGIME_LABEL'),
        value: getLabelFromValue(
          pathOr('-', ['socialSecurities', 0, 'regime'], grantee),
          propOr([], 'regimes', regimeObligatoire)
        )
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_SOUSCRIPTION') ||
            getMiddleBoolean('BENEFICIAIRE_NSS_EST_DEMANDE_DONNEES_CONTRAT')) &&
          attachedToSelf,
        label: getMiddleText('BENEFICIAIRE_NSS_LABEL'),
        value: formatSSNumberKey(pathOr('', ['socialSecurities', 0, 'number'], grantee))
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE'),
        label: getMiddleText('BENEFICIAIRE_NSS_RATT_A_LABEL'),
        value: getLabelFromValue(
          path(['attachedSocialSecurities', 0, 'type'], grantee),
          attachedToOptions
        )
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_NSS_RATT_A_EST_NECESSAIRE') &&
          !attachedToSelf,
        label: 'N°SS de rattachement',
        value: (
          <div>
            {map((ss) => <div key={ss.number}>{formatSSNumberKey(ss.number)}</div>)(
              prop('attachedSocialSecurities', grantee)
            )}
          </div>
        )
      },
      {
        displayed: getMiddleBoolean('BENEFICIAIRE_TELETRANSMISSION_EST_NECESSAIRE'),
        label: getMiddleText('BENEFICIAIRE_TELETRANSMISSION_LABEL'),
        value: path(['socialSecurities', 0, 'isTeletransmission'], grantee) ? 'Oui' : 'Non'
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')) &&
          attachedToSelf,
        label: 'Code régime',
        value: getRegimeCode(grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')) &&
          attachedToSelf,
        label: 'Code caisse',
        value: getCaseNumber(grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_ORG_RATT_EST_NECESSAIRE') &&
          getMiddleBoolean('BENEFICIAIRE_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION')) &&
          attachedToSelf,
        label: 'Centre',
        value: getCenterCode(grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_RANG_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_RANG_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_RANG_LABEL'),
        value: pathOr('-', ['socialSecurities', 0, 'twinsRank'], grantee)
      },
      {
        displayed:
          getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_NECESSAIRE') &&
          (getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('BENEFICIAIRE_ID_TNS_EST_DEMANDE_DONNEES_SOUSCRIPTION')),
        label: getMiddleText('BENEFICIAIRE_ID_TNS_LABEL'),
        value: prop('isTNS', grantee) ? 'Oui' : 'Non'
      }
    ];

    return reject(propEq('displayed', false), granteeCardOptions);
  };

  return {
    methods,
    onSubmit,
    isPaperSignatureMode,
    downloadMutation,
    documentsToUpload,
    sendEmailMutation,
    displayError,
    isProjectEditable,
    $onPrevClick,
    onProjectNotEditable,
    editDetails,
    editSouscription3,
    isDutyOfAdviceSkipped,
    offerCode,
    customerCardOptions: reject(propEq('displayed', false), customerCardOptions),
    guarantees,
    PTIADeathGuarantees,
    spousePensionGuarantees,
    educationPensionGuarantees,
    disabilityGuarantees,
    invalidityGuarantees,
    displayRefundInformation,
    granteesCardOptions,
    editContract,
    canGoNext,
    canSendMail,
    setCanSendMail
  };
};

export default useSubscription4Form;
